import {ExclamationCircleIcon} from '@heroicons/react/outline';
import React, {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {toast} from 'react-toastify';

import {Button, Modal, SpinningLoader} from '@/components';
import CurrencySelector from '@/components/common/CurrencySelector';
import {NumberInput} from '@/components/common/Input';
import type {UserModalProps} from '@/components/DeactivateAccount/types';
import type {AfriexOTCRate, AfriexOTCTrade, RatesMap} from '@/types';
import {AfriexCurrencyCode} from '@/types';
import {formatValues} from '@/utils/dashboard';

interface TradeWithInput extends AfriexOTCTrade {
  inputAmount: number;
}

interface UpdateOTCTradeModalProps extends Pick<
  UserModalProps,
  'isOpen' | 'onClose'
> {
  title: string;
  appRates: {
    old: RatesMap;
    new: RatesMap;
  };
  otcRates: {
    old: AfriexOTCTrade[];
    new: AfriexOTCRate[];
  };
  proceedFn: (newTrades: AfriexOTCTrade[]) => void;
}

const UpdateTradeModal = ({
  isOpen,
  onClose,
  title,
  appRates,
  otcRates,
  proceedFn,
}: UpdateOTCTradeModalProps) => {
  const navigate = useNavigate();
  const initialSymbols = {
    fromSymbol: AfriexCurrencyCode.USD,
    toSymbol: AfriexCurrencyCode.NGN,
  };
  const [availableAmountInput, setAvailableAmountInput] = useState<number>(0);
  const [symbols, setSymbols] =
    useState<Partial<AfriexOTCTrade>>(initialSymbols);
  const [trades, setTrades] = useState<TradeWithInput[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const isLoading = false;

  const oldAppRate =
    appRates?.['old']?.[symbols.fromSymbol as AfriexCurrencyCode]?.[
      symbols.toSymbol as AfriexCurrencyCode
    ];

  const newAppRate =
    appRates?.['new']?.[symbols.fromSymbol as AfriexCurrencyCode]?.[
      symbols.toSymbol as AfriexCurrencyCode
    ];

  const oldOTC = otcRates.old.find(
    (rate) =>
      rate.fromSymbol === symbols.fromSymbol &&
      rate.toSymbol === symbols.toSymbol,
  );

  const newOTCRate = otcRates.new.find(
    (rate) =>
      rate.fromSymbol === symbols.fromSymbol &&
      rate.toSymbol === symbols.toSymbol,
  );

  const handleAddTrade = (e: React.FormEvent) => {
    e.preventDefault();
    const newTrade = {
      ...oldOTC,
      inputAmount: availableAmountInput,
      availableAmount: Number(oldOTC?.availableAmount) + availableAmountInput,
    } as TradeWithInput;

    if (editingIndex !== null) {
      // Update existing trade
      const newTrades = [...trades];
      newTrades[editingIndex] = newTrade;
      setTrades(newTrades);
      setEditingIndex(null);
    } else {
      // Prevent duplicate currency pairs
      const exists = trades.some(
        (trade) =>
          trade.fromSymbol === newTrade.fromSymbol &&
          trade.toSymbol === newTrade.toSymbol,
      );
      if (exists) {
        toast.warn('This currency pair already exists in trades.');
        return;
      }
      setTrades([...trades, newTrade]);
    }

    setAvailableAmountInput(0);
  };

  const handleEditTrade = (index: number) => {
    const tradeToEdit = trades[index];
    setSymbols({
      fromSymbol: tradeToEdit.fromSymbol,
      toSymbol: tradeToEdit.toSymbol,
    });
    setAvailableAmountInput(tradeToEdit.inputAmount);
    setEditingIndex(index);
  };

  const handleDeleteTrade = (index: number) => {
    const newTrades = trades.filter((_, i) => i !== index);
    setTrades(newTrades);
  };

  const handleSubmit = () => {
    const updatedTrades = trades.map((trade) => {
      const {inputAmount: _, ...otcTrade} = trade;
      return otcTrade;
    });
    proceedFn(updatedTrades);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {value} = e.target;
    setAvailableAmountInput(Number(value));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width="w-5/6 md:w-4/6 lg:w-6/12 xl:w-5/12 2xl:w-4/12"
      closeOnOutsideClick
      title={title}
      allowOverflow
    >
      <div className="px-4 pt-3 h-full">
        {isLoading ? (
          <div className="flex w-full h-full justify-center items-center my-5">
            <SpinningLoader />
          </div>
        ) : (
          <div className="flex flex-col gap-5">
            {/* Added Trades List */}
            {trades.length > 0 && (
              <div className="space-y-1">
                {trades.map((trade, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between bg-blue-50 dark:bg-dark-blueBg p-2 rounded"
                  >
                    <div className="flex items-center gap-5 w-full text-gray-800 dark:text-gray-200">
                      <span className="text-xs font-semibold w-1/4">{`${trade.fromSymbol} → ${trade.toSymbol}: `}</span>
                      <span className="text-xs font-medium">
                        {formatValues(
                          Number(trade.availableAmount),
                          'money',
                          6,
                        )}
                      </span>
                    </div>

                    <div className="flex gap-3">
                      <button
                        onClick={() => handleEditTrade(index)}
                        className="text-blue-600 text-xs font-medium"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteTrade(index)}
                        className="text-red-600 text-xs font-medium"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Trade Form */}
            <form onSubmit={handleAddTrade} className="flex flex-col gap-5">
              {/* Corridor Row */}

              <div className="flex flex-col md:flex-row w-full gap-2">
                <div className="flex flex-col md:space-x-1">
                  <span className="text-sm font-medium mb-1 text-gray-700 dark:text-gray-200 ml-1">
                    Currency Pair
                  </span>

                  <div className="flex flex-row gap-1">
                    <CurrencySelector
                      id="symbols"
                      onChange={(
                        event: React.ChangeEvent<HTMLSelectElement>,
                      ) => {
                        setSymbols({
                          ...symbols,
                          fromSymbol: event.target.value as AfriexCurrencyCode,
                        });
                      }}
                      value={symbols.fromSymbol}
                      fixedCurrency={symbols.fromSymbol}
                      className="w-full md:w-24"
                      label=""
                    />

                    <CurrencySelector
                      id="symbols"
                      onChange={(
                        event: React.ChangeEvent<HTMLSelectElement>,
                      ) => {
                        setSymbols({
                          ...symbols,
                          toSymbol: event.target.value as AfriexCurrencyCode,
                        });
                      }}
                      value={symbols.toSymbol}
                      fixedCurrency={symbols.toSymbol}
                      className="w-full md:w-24"
                      label=""
                    />
                  </div>
                </div>

                <NumberInput
                  value={availableAmountInput || ''}
                  className="w-full md:w-36"
                  label={`New ${symbols.toSymbol} Volume*`}
                  required
                  placeholder="Enter volume"
                  max={9999999999}
                  onValueChange={(rawValue) => {
                    handleVolumeChange({
                      target: {
                        name: 'volume',
                        value: rawValue,
                      },
                    } as React.ChangeEvent<HTMLInputElement>);
                  }}
                />

                <div className="flex items-end md:ml-2">
                  <Button type="submit" className="w-full md:w-fit">
                    {editingIndex !== null ? 'Update' : 'Add'}
                  </Button>
                </div>
              </div>

              {!oldOTC ? (
                <div className="w-full flex flex-col gap-3 items-center justify-center text-sm">
                  <p className="flex font-sm gap-2 justify-center text-gray-600 bg-yellow-100 py-2 px-3 rounded-md items-center">
                    <ExclamationCircleIcon className="w-6 h-6 text-orange-500" />
                    No previous OTC Volume found
                  </p>

                  <span>Click the button below to add a new trade</span>

                  <Button
                    onClick={async () => {
                      // react-router 7 types `navigate()` as
                      // `void | Promise<void>`, so the promise form was
                      // floating here. Awaited rather than handled: the reload
                      // below is meant to happen once the history entry is in
                      // place, which is exactly what awaiting gives us. An
                      // async `onClick` is fine - `no-misused-promises` runs
                      // with `checksVoidReturn: {attributes: false}`.
                      await navigate('/rates?tab=otc-trades');
                      window.location.reload();
                    }}
                    variant="outline"
                    colorScheme="cyan"
                    className="my-5"
                  >
                    Visit Trade Page
                  </Button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-6">
                    <div className="space-y-4">
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                          Previous OTC
                        </span>
                        <span className="text-sm font-medium">
                          {formatValues(Number(oldOTC?.rate), 'rate', 6)}
                        </span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                          New OTC
                        </span>
                        <span className="text-sm font-medium">
                          {formatValues(
                            Number(newOTCRate?.value || 0),
                            'rate',
                            6,
                          )}
                        </span>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                          Previous App Rate
                        </span>
                        <span className="text-sm font-medium">
                          {formatValues(Number(oldAppRate), 'rate', 6)}
                        </span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                          New App Rate
                        </span>
                        <span className="text-sm font-medium">
                          {formatValues(Number(newAppRate), 'rate', 6)}
                        </span>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                          Remaining Volume
                        </span>
                        <span className="text-sm font-medium">
                          {formatValues(
                            Number(oldOTC?.availableAmount),
                            'money',
                            6,
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Change Submit button to Add */}
                  <div className="flex justify-center mb-3">
                    <Button
                      type="button"
                      onClick={handleSubmit}
                      variant="solid"
                      colorScheme="green"
                      disabled={!(trades.length > 0)}
                    >
                      Submit Rates
                    </Button>
                  </div>
                </>
              )}
            </form>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default UpdateTradeModal;
